import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import { formatRelativeTime, getStatusBadgeClass, classNames } from '../lib/utils';
import { Dropdown } from '../components/Dropdown';
import {
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  ExternalLink,
  Globe,
  Server,
  X,
} from 'lucide-react';
import type { Account, CreateAccountRequest, TestConnectionResponse } from '../lib/types';

interface AccountFormState {
  account_id: string;
  alias: string;
  role_arn: string;
  external_id: string;
  enabled_regions: string[];
}

export function AccountsPage() {
  const { idToken: accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState<AccountFormState>({
    account_id: '',
    alias: '',
    role_arn: '',
    external_id: '',
    enabled_regions: ['us-east-1'],
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);

  const allRegions = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1', 'eu-central-2', 'eu-north-1', 'eu-south-1', 'eu-south-2',
    'ap-south-1', 'ap-northeast-1', 'ap-northeast-2', 'ap-northeast-3', 'ap-southeast-1', 'ap-southeast-2', 'ap-southeast-3', 'ap-southeast-4',
    'ca-central-1', 'ca-west-1',
    'sa-east-1',
    'me-central-1', 'me-south-1',
    'af-south-1',
  ];

  const fetchAccounts = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await api.accounts.list(accessToken);
      setAccounts(res.accounts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [accessToken]);

  const resetForm = () => {
    setFormData({
      account_id: '',
      alias: '',
      role_arn: '',
      external_id: '',
      enabled_regions: ['us-east-1'],
    });
    setFormErrors({});
    setTestResult(null);
  };

  const openAddModal = () => {
    resetForm();
    setEditingAccount(null);
    setShowModal(true);
  };

  const openEditModal = (account: Account) => {
    setFormData({
      account_id: account.account_id,
      alias: account.alias,
      role_arn: account.role_arn,
      external_id: '',
      enabled_regions: account.enabled_regions,
    });
    setEditingAccount(account);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAccount(null);
    resetForm();
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.account_id.trim()) errors.account_id = 'Account ID is required';
    else if (!/^\d{12}$/.test(formData.account_id)) errors.account_id = 'Account ID must be 12 digits';
    if (!formData.alias.trim()) errors.alias = 'Alias is required';
    if (!formData.role_arn.trim()) errors.role_arn = 'Role ARN is required';
    else if (!formData.role_arn.startsWith('arn:aws:iam::')) errors.role_arn = 'Invalid role ARN format';
    if (formData.enabled_regions.length === 0) errors.enabled_regions = 'Select at least one region';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setError('');

    try {
      const payload: CreateAccountRequest = {
        account_id: formData.account_id.trim(),
        alias: formData.alias.trim(),
        role_arn: formData.role_arn.trim(),
        external_id: formData.external_id.trim() || undefined,
        enabled_regions: formData.enabled_regions,
      };

      if (editingAccount) {
        await api.accounts.update(accessToken!, editingAccount.account_id, {
          alias: payload.alias,
          enabled_regions: payload.enabled_regions,
        });
      } else {
        await api.accounts.create(accessToken!, payload);
      }
      closeModal();
      fetchAccounts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestConnection = async (accountId: string) => {
    setTestingConnection(accountId);
    setTestResult(null);
    try {
      const result = await api.accounts.testConnection(accessToken!, accountId);
      setTestResult(result);
    } catch (err) {
      setTestResult({ status: 'error', detail: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDelete = async (accountId: string) => {
    if (!window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) return;
    setError('');
    try {
      await api.accounts.delete(accessToken!, accountId);
      fetchAccounts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete account');
    }
  };

  const toggleRegion = (region: string) => {
    setFormData((prev) => ({
      ...prev,
      enabled_regions: prev.enabled_regions.includes(region)
        ? prev.enabled_regions.filter((r) => r !== region)
        : [...prev.enabled_regions, region],
    }));
  };

  if (isLoading) {
    return (
      <div className="page-accounts">
        <div className="page-header">
          <h1 className="page-title">Accounts</h1>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="empty-state">
              <Loader2 size={48} className="empty-state-icon spin" />
              <h3 className="empty-state-title">Loading accounts...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-accounts">
      <header className="page-header">
        <h1 className="page-title">Accounts</h1>
        <button className="btn btn-primary" onClick={openAddModal}>
          <Plus size={18} /> Add Account
        </button>
      </header>

      {error && (
        <div className="login-alert" style={{ marginBottom: 'var(--space-4)' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="card">
        {accounts.length === 0 ? (
          <div className="card-content empty-state">
            <div className="empty-state-icon" style={{ color: 'var(--clr-teal)' }}>
              <Server size={64} />
            </div>
            <h3 className="empty-state-title">No accounts connected</h3>
            <p className="empty-state-text">Add your first AWS account to start monitoring resources and costs</p>
            <button className="btn btn-primary" onClick={openAddModal} style={{ marginTop: 'var(--space-4)' }}>
              <Plus size={18} /> Add Account
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Alias</th>
                  <th>Regions</th>
                  <th>Status</th>
                  <th>Last Scanned</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.account_id}>
                    <td>
                      <code style={{ fontSize: 'var(--text-xs)' }}>{account.account_id}</code>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600 }}>{account.alias}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                        {account.enabled_regions.slice(0, 3).map((region) => (
                          <span key={region} className="badge badge-info" style={{ fontSize: 'var(--text-xs)' }}>
                            <Globe size={10} /> {region}
                          </span>
                        ))}
                        {account.enabled_regions.length > 3 && (
                          <span className="badge badge-neutral" style={{ fontSize: 'var(--text-xs)' }}>
                            +{account.enabled_regions.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span className={classNames('status-dot', account.status)} />
                        <span className={classNames('badge', `badge-${getStatusBadgeClass(account.status)}`)}>
                          {account.status}
                        </span>
                      </div>
                    </td>
                    <td>
                      {account.last_scanned_at ? formatRelativeTime(account.last_scanned_at) : 'Never'}
                    </td>
                    <td>
                      <Dropdown
                        trigger={({ onClick }) => (
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            aria-label={`Actions for ${account.alias}`}
                            onClick={onClick}
                          >
                            <ExternalLink size={16} />
                          </button>
                        )}
                      >
                        {(close) => (
                          <>
                            <button
                              className="dropdown-item"
                              onClick={() => { close(); openEditModal(account); }}
                            >
                              <Edit size={16} /> Edit
                            </button>
                            <button
                              className="dropdown-item"
                              onClick={() => { close(); handleTestConnection(account.account_id); }}
                              disabled={testingConnection === account.account_id}
                            >
                              <RefreshCw size={16} className={testingConnection === account.account_id ? 'spin' : ''} />
                              {testingConnection === account.account_id ? 'Testing...' : 'Test Connection'}
                            </button>
                            <div className="dropdown-divider" />
                            <button
                              className="dropdown-item danger"
                              onClick={() => { close(); handleDelete(account.account_id); }}
                            >
                              <Trash2 size={16} /> Delete
                            </button>
                          </>
                        )}
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h2 id="modal-title" className="modal-title">
                {editingAccount ? 'Edit Account' : 'Add AWS Account'}
              </h2>
              <button className="modal-close" onClick={closeModal} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {!editingAccount && (
                  <div className="form-group">
                    <label htmlFor="account_id" className="form-label">AWS Account ID</label>
                    <input
                      type="text"
                      id="account_id"
                      className={classNames('form-input', { error: !!formErrors.account_id })}
                      value={formData.account_id}
                      onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                      placeholder="123456789012"
                      maxLength={12}
                      disabled={submitting}
                    />
                    {formErrors.account_id && <p className="form-error">{formErrors.account_id}</p>}
                    <p className="form-hint">12-digit AWS account ID</p>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="alias" className="form-label">Alias</label>
                  <input
                    type="text"
                    id="alias"
                    className={classNames('form-input', { error: !!formErrors.alias })}
                    value={formData.alias}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                    placeholder="e.g., Production, Staging, Development"
                    disabled={submitting}
                  />
                  {formErrors.alias && <p className="form-error">{formErrors.alias}</p>}
                  <p className="form-hint">Display name for this account</p>
                </div>

                <div className="form-group">
                  <label htmlFor="role_arn" className="form-label">Role ARN</label>
                  <input
                    type="text"
                    id="role_arn"
                    className={classNames('form-input', { error: !!formErrors.role_arn })}
                    value={formData.role_arn}
                    onChange={(e) => setFormData({ ...formData, role_arn: e.target.value })}
                    placeholder="arn:aws:iam::123456789012:role/vedniq-monitor-readonly"
                    disabled={submitting}
                  />
                  {formErrors.role_arn && <p className="form-error">{formErrors.role_arn}</p>}
                  <p className="form-hint">Cross-account IAM role ARN with ReadOnlyAccess + Cost Explorer permissions</p>
                </div>

                {!editingAccount && (
                  <div className="form-group">
                    <label htmlFor="external_id" className="form-label">External ID (optional)</label>
                    <input
                      type="text"
                      id="external_id"
                      className="form-input"
                      value={formData.external_id}
                      onChange={(e) => setFormData({ ...formData, external_id: e.target.value })}
                      placeholder="Auto-generated if left blank"
                      disabled={submitting}
                    />
                    <p className="form-hint">Leave blank to auto-generate. Must match the External ID in the target account's trust policy.</p>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Enabled Regions</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                    {allRegions.map((region) => (
                      <label
                        key={region}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)',
                          padding: 'var(--space-1) var(--space-2)',
                          border: '1px solid var(--slate-300)',
                          borderRadius: 'var(--radius-pill)',
                          fontSize: 'var(--text-xs)',
                          cursor: 'pointer',
                          background: formData.enabled_regions.includes(region) ? 'var(--color-primary-soft)' : 'transparent',
                          borderColor: formData.enabled_regions.includes(region) ? 'var(--color-primary)' : 'var(--slate-300)',
                          color: formData.enabled_regions.includes(region) ? 'var(--color-primary-dark)' : 'var(--slate-700)',
                          transition: 'all var(--tx-fast)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.enabled_regions.includes(region)}
                          onChange={() => toggleRegion(region)}
                          style={{ width: 14, height: 14, accentColor: 'var(--color-primary)' }}
                        />
                        <Globe size={12} style={{ flexShrink: 0 }} />
                        {region}
                      </label>
                    ))}
                  </div>
                  {formErrors.enabled_regions && <p className="form-error">{formErrors.enabled_regions}</p>}
                  <p className="form-hint">Only enabled regions will be scanned. Selecting more regions increases scan time and API calls.</p>
                </div>
              </div>
              <footer className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="spin" /> Saving...
                    </>
                  ) : (
                    editingAccount ? 'Save Changes' : 'Add Account'
                  )}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Test Connection Result Modal */}
      {testResult && (
        <div className="modal-overlay" onClick={() => setTestResult(null)} role="dialog" aria-modal="true" aria-labelledby="test-result-title">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h2 id="test-result-title" className="modal-title">Connection Test Result</h2>
              <button className="modal-close" onClick={() => setTestResult(null)} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto var(--space-4)',
                background: testResult.status === 'connected' ? 'var(--color-primary-light)' : '#fef2f2',
                color: testResult.status === 'connected' ? 'var(--color-primary-dark)' : 'var(--color-red-dark)',
              }}>
                {testResult.status === 'connected' ? (
                  <CheckCircle size={32} />
                ) : (
                  <AlertCircle size={32} />
                )}
              </div>
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 'var(--space-2)' }}>
                {testResult.status === 'connected' ? 'Connection Successful' : 'Connection Failed'}
              </h3>
              {testResult.assumed_arn && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-600)', marginBottom: 'var(--space-2)' }}>
                  Assumed role: <code style={{ wordBreak: 'break-all' }}>{testResult.assumed_arn}</code>
                </p>
              )}
              {testResult.detail && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-red)', fontFamily: 'monospace' }}>
                  {testResult.detail}
                </p>
              )}
            </div>
            <footer className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={() => setTestResult(null)}>Close</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}